import React from 'react';
import PropTypes from 'prop-types';
import BeRemoteErrorAlert from 'client/components/Be/BeRemoteErrorAlert';
import BeLoading from 'client/components/Be/BeLoading';
import { ButtonWaiting } from 'client/components/Button/ButtonWaiting';

/**
 * @component EATDTesterIncident
 * @description Renders a single incident item for testing EATD rules, including controls and results.
 *              This component expects to be rendered within a list structure managed by a parent.
 */
function EATDTesterIncident ({
  incidentId,
  incidentNumber,
  onApplyChanges,
  onRemove,
  isApplying,
  isPreviewing,
  error,
  applyResult,
  onPreviewChanges,
  previewResult,
}) {
  // Extract incident information from previewResult if available
  const incidentData = previewResult?.data?.original || {};
  const anonymizedData = previewResult?.data?.anonymized || {};
  const displayNumber = incidentNumber || incidentData?.number || 'Unknown';
  const displayName = `${displayNumber} (${incidentData?.caller?.dynamicName || 'No caller'})`;
  
  // Check if incident is archived
  const isArchived = incidentData?.archived === true || incidentData?.status === 'archived';

  /**
   * @function parseCallerValue
   * @description Parses a caller value that might be in the special format 'id:{callerId}'
   * @param {string|object|null} callerValue - Caller value to parse
   * @returns {object|null} Parsed caller object or null
   */
  const parseCallerValue = (callerValue) => {
    if (!callerValue) return null;
    
    // Handle the special format: id:{callerId}
    if (typeof callerValue === 'string' && callerValue.startsWith('id:')) {
      // Extract the ID from within the curly braces
      const matches = callerValue.match(/id:{(.+)}/);
      if (matches && matches[1]) {
        const callerId = matches[1];
        return { id: callerId };
      }
    }
    
    // If it's a string that looks like JSON, try to parse it
    if (typeof callerValue === 'string' && 
        (callerValue.startsWith('{') || callerValue.includes('"id":'))) {
      try {
        return JSON.parse(callerValue);
      } catch (e) {
        return { id: callerValue }; // Fallback to treating the string as ID
      }
    }
    
    // If it's already an object, return it
    if (typeof callerValue === 'object') {
      return callerValue;
    }
    
    // Fallback for plain string IDs
    return { id: callerValue };
  };

  /**
   * @function handleApplyChangesClick
   * @description Calls the onApplyChanges prop for this specific incident.
   */
  const handleApplyChangesClick = () => {
    onApplyChanges(incidentId);
  };

  /**
   * @function handlePreviewChangesClick
   * @description Calls the onPreviewChanges prop for this specific incident.
   */
  const handlePreviewChangesClick = () => {
    onPreviewChanges(incidentId);
  };

  /**
   * @function handleRemoveClick
   * @description Calls the onRemove prop for this specific incident.
   */
  const handleRemoveClick = () => {
    if (window.confirm('Are you sure you want to remove this incident from the test list?')) {
      onRemove(incidentId);
    }
  };

  // Parse replacement caller if present
  const replacementCaller = anonymizedData?.caller
    ? parseCallerValue(anonymizedData.caller)
    : null;

  const stringifyValue = (value) => {
    if (value === null || value === undefined || value === '') return '(empty)';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const flattenChanges = (value, prefix = '') => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return prefix ? [{ field: prefix, newValue: value }] : [];
    }

    return Object.entries(value).reduce((changes, [key, nestedValue]) => {
      const field = prefix ? `${prefix}.${key}` : key;
      if (nestedValue && typeof nestedValue === 'object' && !Array.isArray(nestedValue)) {
        return [...changes, ...flattenChanges(nestedValue, field)];
      }
      return [...changes, { field, newValue: nestedValue }];
    }, []);
  };

  const fieldChanges = flattenChanges(anonymizedData)
    .filter(change => !change.field.startsWith('_'))
    .map(change => ({
      ...change,
      originalValue: change.field.split('.').reduce((result, key) => (result ? result[key] : undefined), incidentData),
    }));

  return (
    <div className="list-group-item">
      <h4 className="list-group-item-heading mb-3">
        <div className="name pull-left" style={{ lineHeight: '30px' }}>
          <i className="icon mdi mdi-ticket xs-mr-5"></i>
          {displayName}
          {isArchived && (
            <span className="badge badge-secondary ml-2" title="This incident is archived">
              <i className="mdi mdi-archive"></i> Archived
            </span>
          )}
        </div>
        <span className="btn-group pull-right">
          <ButtonWaiting
            type="button"
            className="btn btn-rounded btn-info"
            onClick={handlePreviewChangesClick}
            isWaiting={isPreviewing}
            disabled={isApplying || isPreviewing}
            title="Preview Changes for this Incident"
          >
            <i className='icon icon-left mdi mdi-eye-outline'></i>{' '}
            Preview
          </ButtonWaiting>
          <ButtonWaiting
            type="button"
            className="btn btn-rounded btn-warning"
            onClick={handleApplyChangesClick}
            isWaiting={isApplying}
            disabled={isApplying || isPreviewing || isArchived}
            title={isArchived ? 'Cannot apply changes to archived incidents' : 'Apply EATD Rules to this Incident'}
          >
            <i className='icon icon-left mdi mdi-pencil-outline'></i>{' '}
            Apply Changes
          </ButtonWaiting>
          <button
            type="button"
            className="btn btn-rounded btn-default"
            onClick={handleRemoveClick}
            disabled={isApplying || isPreviewing}
            title="Remove Incident from Test List"
          >
            <i className='icon icon-left mdi mdi-delete'></i>{' '}
            Remove
          </button>
        </span>
        <div className="clearfix"></div>
      </h4>

      {(isApplying || isPreviewing) && (
        <BeLoading size={20}>
          {isApplying ? `Applying changes to incident ${displayNumber}...` : `Previewing changes for incident ${displayNumber}...`}
        </BeLoading>
      )}

      {error && <BeRemoteErrorAlert error={error} />}

      {/* Display Preview Result */}
      {previewResult && !error && !isPreviewing && (
        <div
          className="alert mt-2 p-2"
          style={{ backgroundColor: '#2b6cb0', color: '#ffffff', borderColor: '#1e4e8c' }}
        >
          <h6>Preview Results:</h6>
          
          {/* Warning for archived incidents */}
          {isArchived && (
            <div className="alert alert-warning py-2 px-3 mb-2">
              <strong><i className="mdi mdi-alert-triangle"></i> Archived Incident Warning:</strong>
              <p className="mb-0">This incident is archived and cannot be modified in TOPdesk. Only preview operations are available for archived incidents.</p>
            </div>
          )}
          
          {/* Show notices if any */}
          {previewResult.notices && previewResult.notices.length > 0 && (
            <div>
              <p>Notices:</p>
              <ul>
                {previewResult.notices.map((notice, idx) => (
                  <li key={idx}>{notice.action} {notice.object}: {notice.id}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Enhanced caller replacement info */}
          {previewResult.data && (
            <div className="mb-2">
              {/* Current caller info with more details */}
              <div className="p-2 border-left border-info">
                <div className="mb-1">
                  <strong>Original Caller:</strong>
                  <span className="ml-2">
                    {previewResult.data.original?.caller?.dynamicName || previewResult.data.original?.caller?.name || '(No name)'}
                  </span>
                </div>
                {previewResult.data.original?.caller?.email && (
                  <div className="small ml-3" style={{ color: '#ffffff' }}>
                    Email: {previewResult.data.original?.caller?.email}
                  </div>
                )}
                {previewResult.data.original?.caller?.id && (
                  <div className="small ml-3" style={{ color: '#ffffff' }}>
                    ID: {previewResult.data.original?.caller?.id}
                  </div>
                )}
              </div>
              
              {/* Replacement caller with source information and more details */}
              {replacementCaller && (
                <div className="p-2 mt-2 border-left border-warning">
                  <div className="mb-1">
                    <strong>Replacement Caller:</strong>
                    <span className="ml-2">
                      {replacementCaller.dynamicName || replacementCaller.name || '(No name)'}
                    </span>
                    
                    {/* Badge indicating source of replacement */}
                    <span className="badge badge-warning ml-2">Caller will be replaced</span>
                  </div>
                  
                  {/* Additional caller details */}
                  <div className="small ml-3" style={{ color: '#ffffff' }}>
                    ID: {replacementCaller.id || '(Missing ID)'}
                  </div>
                  
                  {replacementCaller.email && (
                    <div className="small ml-3" style={{ color: '#ffffff' }}>
                      Email: {replacementCaller.email}
                    </div>
                  )}
                  
                </div>
              )}
              
              {/* No replacement message */}
              {!replacementCaller && (
                <div className="mt-2 text-muted small">
                  <i className="mdi mdi-information-outline"></i> Caller will not be replaced.
                </div>
              )}
            </div>
          )}
          
          {/* List other field changes */}
          {fieldChanges.length > 0 && (
            <div className="mb-2">
              <h6 className="mb-1">Field Changes:</h6>
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Original Value</th>
                    <th>New Value</th>
                  </tr>
                </thead>
                <tbody>
                  {fieldChanges.map((change, idx) => (
                    <tr key={idx}>
                      <td>{change.field}</td>
                      <td className="text-muted">{stringifyValue(change.originalValue)}</td>
                      <td className="font-weight-bold">{stringifyValue(change.newValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {Array.isArray(previewResult?.data?.attachmentsToDelete) && previewResult.data.attachmentsToDelete.length > 0 && (
            <div className="mb-2">
              <h6 className="mb-1">Attachments To Delete:</h6>
              <ul className="mb-0">
                {previewResult.data.attachmentsToDelete.map(attachment => (
                  <li key={attachment.id}>
                    {attachment.fileName || attachment.id}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Show raw preview data for debugging (collapsible) */}
          <details className="mt-3">
            <summary className="text-light small">Raw Preview Data</summary>
            <pre className="bg-light text-dark p-2 rounded mb-0 small mt-2">
              <code>{JSON.stringify(previewResult.data, null, 2)}</code>
            </pre>
          </details>
          
          {!previewResult.data && !previewResult.notices?.length && <p>Preview successful, no changes proposed.</p>}
        </div>
      )}

      {/* Display Apply Result */}
      {applyResult && !error && !isApplying && (
        <div className={`alert ${applyResult.ok ? 'alert-success' : 'alert-warning'} mt-2 p-2`}>
          <h6>Apply Changes Results:</h6>
          {applyResult.notices && applyResult.notices.length > 0 && (
            <div>
              <p>Applied Changes:</p>
              <ul>
                {applyResult.notices.map((notice, idx) => (
                  <li key={idx}>{notice.action} {notice.object}: {notice.id}</li>
                ))}
              </ul>
            </div>
          )}
          {applyResult.data && (
            <pre className="bg-light p-2 rounded mb-0 small">
              <code>{JSON.stringify(applyResult.data, null, 2)}</code>
            </pre>
          )}
          {!applyResult.data && !applyResult.notices?.length && applyResult.ok && <p>Apply successful, no changes made.</p>}
        </div>
      )}
    </div>
  );
}

EATDTesterIncident.propTypes = {
  incidentId: PropTypes.string.isRequired,
  incidentNumber: PropTypes.string,
  onApplyChanges: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onPreviewChanges: PropTypes.func.isRequired,
  isApplying: PropTypes.bool,
  isPreviewing: PropTypes.bool,
  error: PropTypes.object,
  applyResult: PropTypes.object,
  previewResult: PropTypes.object,
};

EATDTesterIncident.defaultProps = {
  incidentNumber: null,
  isApplying: false,
  isPreviewing: false,
  error: null,
  applyResult: null,
  previewResult: null,
};

export default EATDTesterIncident; 